/**
 * Transaction validation schemas - ALIGNED WITH DATABASE SCHEMA
 */

import { z } from 'zod';
import { CURRENCY_CODES } from '../constants/currencies';

/**
 * Budget category options from schema
 */
const budgetCategoryEnum = z.enum([
  'Receitas Operacionais',
  'Custos dos Produtos / Serviços',
  'OPEX (Despesas Operacionais)',
  'Impostos pagos',
  'CAPEX / Compra de Ativos',
  'Venda de Ativos',
  'Outras Receitas não operacionais',
  'Capital / Aportes de Sócios',
  'Empréstimos recebidos',
  'Amortização de empréstimos',
  'Distribuição de Lucros / Dividendos / JCP',
  'Adiantamentos a Sócios',
  'Devolução de Adiantamentos',
], {
  errorMap: () => ({ message: 'Categoria orçamentária inválida' }),
});

/**
 * Transaction create schema - EXACT MATCH WITH cash_flow_entries TABLE
 */
export const transactionCreateSchema = z.object({
  // Entry type (required)
  entry_type: z.enum(['income', 'expense', 'transfer'], {
    errorMap: () => ({ message: 'Tipo de lançamento inválido' }),
  }),
  
  // Description (required)
  description: z
    .string({ required_error: 'Descrição é obrigatória' })
    .min(1, 'Descrição é obrigatória')
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .trim(),
  
  // Notes (optional)
  notes: z
    .string()
    .max(2000, 'Notas devem ter no máximo 2000 caracteres')
    .optional()
    .nullable(),
  
  // Amount (required)
  amount: z
    .number({
      required_error: 'Valor é obrigatório',
      invalid_type_error: 'Valor deve ser um número',
    })
    .positive('Valor deve ser maior que zero')
    .max(999999999.99, 'Valor muito alto')
    .refine(
      (val) => {
        const decimals = (val.toString().split('.')[1] || '').length;
        return decimals <= 2;
      },
      'Valor deve ter no máximo 2 casas decimais'
    ),
  
  // Currency (optional, default BRL)
  currency: z
    .enum(CURRENCY_CODES as [string, ...string[]], {
      errorMap: () => ({ message: 'Moeda inválida' }),
    })
    .default('BRL')
    .optional(),
  
  // Categorization (required fields)
  main_category: z
    .string({ required_error: 'Categoria principal é obrigatória' })
    .min(1, 'Categoria principal é obrigatória')
    .max(100, 'Categoria principal deve ter no máximo 100 caracteres')
    .trim(),
  
  subcategory: z
    .string()
    .max(100, 'Subcategoria deve ter no máximo 100 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  classification: z
    .enum(['Fixo', 'Variável', 'Eventual'], {
      errorMap: () => ({ message: 'Classificação inválida' }),
    })
    .default('Eventual'),
  
  budget_category: budgetCategoryEnum.default('OPEX (Despesas Operacionais)'),
  
  cost_center: z
    .string()
    .max(100, 'Centro de custo deve ter no máximo 100 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  project_id: z
    .string()
    .uuid('ID do projeto inválido')
    .optional()
    .nullable(),
  
  // Dates
  entry_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida. Use YYYY-MM-DD')
    .refine(
      (date) => !isNaN(new Date(date).getTime()),
      'Data inválida'
    )
    .optional()
    .nullable(),
  
  due_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida. Use YYYY-MM-DD')
    .refine(
      (date) => !isNaN(new Date(date).getTime()),
      'Data inválida'
    )
    .optional()
    .nullable(),
  
  payment_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida. Use YYYY-MM-DD')
    .refine(
      (date) => !isNaN(new Date(date).getTime()),
      'Data inválida'
    )
    .optional()
    .nullable(),
  
  // Recurrence
  is_recurring: z
    .boolean()
    .default(false),
  
  frequency: z
    .enum(['daily', 'weekly', 'monthly', 'quarterly', 'yearly'], {
      errorMap: () => ({ message: 'Frequência inválida' }),
    })
    .optional()
    .nullable(),
  
  recurrence_end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida. Use YYYY-MM-DD')
    .refine(
      (date) => !isNaN(new Date(date).getTime()),
      'Data inválida'
    )
    .optional()
    .nullable(),
  
  // Payment info
  payment_status: z
    .enum(['confirmado', 'previsão'], {
      errorMap: () => ({ message: 'Status de pagamento inválido' }),
    })
    .default('previsão'),
  
  payment_method: z
    .string()
    .max(100, 'Método de pagamento deve ter no máximo 100 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  account_name: z
    .string()
    .max(100, 'Nome da conta deve ter no máximo 100 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  payment_details: z
    .string()
    .max(500, 'Detalhes do pagamento devem ter no máximo 500 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  // Additional info
  invoice_number: z
    .string()
    .max(100, 'Número da nota deve ter no máximo 100 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  tax_category: z
    .enum(['Dedutível', 'Não Dedutível', 'Isento'], {
      errorMap: () => ({ message: 'Categoria tributária inválida' }),
    })
    .default('Dedutível'),
  
  business_partner: z
    .string()
    .max(200, 'Parceiro comercial deve ter no máximo 200 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  location: z
    .string()
    .max(200, 'Localização deve ter no máximo 200 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  stakeholders: z
    .string()
    .max(500, 'Stakeholders devem ter no máximo 500 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  document_source: z
    .string()
    .max(200, 'Fonte do documento deve ter no máximo 200 caracteres')
    .trim()
    .optional()
    .nullable(),
  
  tags: z
    .array(z.string())
    .max(20, 'Máximo de 20 tags')
    .optional()
    .nullable(),
  
  // Simulation flag
  is_simulation: z
    .boolean()
    .default(false),
    
}).refine(
  (data) => {
    // If recurring, frequency is required
    if (data.is_recurring) {
      return !!data.frequency;
    }
    return true;
  },
  {
    message: 'Lançamentos recorrentes devem ter uma frequência',
    path: ['frequency'],
  }
).refine(
  (data) => {
    // If payment status is "confirmado", payment_date is required
    if (data.payment_status === 'confirmado') {
      return !!data.payment_date;
    }
    return true;
  },
  {
    message: 'Data de pagamento é obrigatória para lançamentos confirmados',
    path: ['payment_date'],
  }
)
// Removida validação de due_date >= entry_date para permitir flexibilidade
// em lançamentos retroativos onde a data de vencimento pode ser anterior à data do lançamento
// Removida validação de payment_date >= entry_date para permitir flexibilidade
// em conciliações bancárias onde a data real pode ser diferente
.refine(
  (data) => {
    // Recurrence end date must be > entry date if both exist
    if (data.recurrence_end_date && data.entry_date) {
      return new Date(data.recurrence_end_date) > new Date(data.entry_date);
    }
    return true;
  },
  {
    message: 'Data final da recorrência deve ser posterior à data do lançamento',
    path: ['recurrence_end_date'],
  }
);

export type TransactionCreateData = z.infer<typeof transactionCreateSchema>;

/**
 * Transaction update schema (all fields optional)
 */
export const transactionUpdateSchema = transactionCreateSchema.partial();

export type TransactionUpdateData = z.infer<typeof transactionUpdateSchema>;

/**
 * Transaction filter schema
 */
export const transactionFilterSchema = z.object({
  entry_type: z
    .enum(['income', 'expense', 'transfer'])
    .or(z.array(z.enum(['income', 'expense', 'transfer'])))
    .optional(),
  
  payment_status: z
    .enum(['confirmado', 'previsão'])
    .or(z.array(z.enum(['confirmado', 'previsão'])))
    .optional(),
  
  main_category: z
    .string()
    .optional(),
  
  subcategory: z
    .string()
    .optional(),
  
  classification: z
    .enum(['Fixo', 'Variável', 'Eventual'])
    .optional(),
  
  budget_category: budgetCategoryEnum.optional(),
  
  tags: z
    .array(z.string())
    .optional(),
  
  date_from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  
  date_to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  
  amount_min: z
    .number()
    .nonnegative()
    .optional(),
  
  amount_max: z
    .number()
    .positive()
    .optional(),
  
  search: z
    .string()
    .optional(),
  
  is_simulation: z
    .boolean()
    .optional(),
});

export type TransactionFilterData = z.infer<typeof transactionFilterSchema>;
